'use client';

import Link from 'next/link';
import { Book, Code, Lightbulb, Wrench, ExternalLink, ArrowRight, Braces } from 'lucide-react';
import { StandardLayout } from '@/app/components';
import { useSite } from '@/lib/site-context';
import '@/app/components/editor/Documentation.scss';

export default function DocsPage() {
  const site = useSite();
  return (
    <StandardLayout>
      <div className="content-wrapper">
        <div className="docs-container">
          {/* Header */}
          <section className="docs-hero">
            <div className="hero-glow"></div>
            <h1>Documentation</h1>
            <p className="hero-description">
              Learn how to create amazing 3D printing designs with {site.codeLanguageLabel} {site.id === 'py' && 'and FullControl'}
            </p>
          </section>          {/* Quick Start */}
          <section className="docs-section">
            <h2>
              <span className="section-number">01. </span>
              Quick Start
            </h2>
            <p className="section-intro">
              New to {site.displayName}? Start here to get up and running in minutes.
            </p>
            <div className="quick-start-grid">
              <Link href="/docs/getting-started" className="docs-card quick-start-card">
                <div className="docs-card-icon">
                  <Lightbulb />
                </div>
                <div className="card-content-wrapper">
                  <h3>Getting Started</h3>
                  <p>Your first {site.displayName} sketch in 5 minutes</p>
                  <div className="card-footer-action">
                    <span className="action-text">Start learning</span>
                    <ArrowRight className="card-arrow" />
                  </div>
                </div>
              </Link>
              <Link href="/docs/basics" className="docs-card quick-start-card">
                <div className="docs-card-icon">
                  <Code />
                </div>
                <div className="card-content-wrapper">
                  <h3>FullControl Basics</h3>
                  <p>Learn the fundamentals of FullControl</p>
                  <div className="card-footer-action">
                    <span className="action-text">Explore basics</span>
                    <ArrowRight className="card-arrow" />
                  </div>
                </div>
              </Link>
            </div>
          </section>          {/* Main Documentation Sections */}
          <section className="docs-section">
            <h2>
              <span className="section-number">02. </span>
              Documentation Sections
            </h2>
            <p className="section-intro">
              Comprehensive guides covering all aspects of FullControl development.
            </p>
            <div className="docs-grid">
              <Link href="/docs/fundamentals" className="docs-card feature-card">
                <div className="docs-card-icon">
                  <Book />
                </div>
                <div className="card-content-wrapper">
                  <h3>Fundamentals</h3>
                  <p>Core concepts and principles of parametric 3D printing design</p>
                  <div className="card-tags">
                    <span className="tag">Essential</span>
                    <span className="tag">Theory</span>
                  </div>
                  <div className="card-footer-action">
                    <span className="action-text">Learn fundamentals</span>
                    <ArrowRight className="card-arrow" />
                  </div>
                </div>
              </Link>

              <Link href="/docs/library" className="docs-card feature-card">
                <div className="docs-card-icon">
                  <Braces />
                </div>
                <div className="card-content-wrapper">
                  <h3>Library API</h3>
                  <p>Comprehensive reference for {site.codeLanguageLabel} helpers available in the editor</p>
                  <div className="card-tags">
                    <span className="tag">Reference</span>
                    <span className="tag">API</span>
                  </div>
                  <div className="card-footer-action">
                    <span className="action-text">Open reference</span>
                    <ArrowRight className="card-arrow" />
                  </div>
                </div>
              </Link>

              <Link href="/docs/geometry" className="docs-card feature-card">
                <div className="docs-card-icon">
                  <Code />
                </div>
                <div className="card-content-wrapper">
                  <h3>Geometry Functions</h3>
                  <p>Points, lines, arcs, and complex geometric operations</p>
                  <div className="card-tags">
                    <span className="tag">Functions</span>
                    <span className="tag">Math</span>
                  </div>
                  <div className="card-footer-action">
                    <span className="action-text">Explore geometry</span>
                    <ArrowRight className="card-arrow" />
                  </div>
                </div>
              </Link>

              <Link href="/docs/printing" className="docs-card feature-card">
                <div className="docs-card-icon">
                  <Wrench />
                </div>
                <div className="card-content-wrapper">
                  <h3>Printing Controls</h3>
                  <p>Extrusion, temperature, speed, and printer settings</p>
                  <div className="card-tags">
                    <span className="tag">Hardware</span>
                    <span className="tag">Control</span>
                  </div>
                  <div className="card-footer-action">
                    <span className="action-text">Master printing</span>
                    <ArrowRight className="card-arrow" />
                  </div>
                </div>
              </Link>

              <Link href="/docs/examples" className="docs-card feature-card">
                <div className="docs-card-icon">
                  <Lightbulb />
                </div>
                <div className="card-content-wrapper">
                  <h3>Examples & Tutorials</h3>
                  <p>Step-by-step guides and real-world examples</p>
                  <div className="card-tags">
                    <span className="tag">Practical</span>
                    <span className="tag">Demos</span>
                  </div>
                  <div className="card-footer-action">
                    <span className="action-text">See examples</span>
                    <ArrowRight className="card-arrow" />
                  </div>
                </div>
              </Link>
            </div>
          </section>          {/* External Resources */}
          <section className="docs-section">
            <h2>
              <span className="section-number">03. </span>
              External Resources
            </h2>
            <p className="section-intro">
              Additional resources and official documentation to expand your knowledge.
            </p>
            <div className="external-resources">
              <a
                href="https://fullcontrol.xyz/"
                target="_blank"
                rel="noopener noreferrer"
                className="resource-link primary-resource"
              >
                <div className="resource-content">
                  <span className="resource-title">Official FullControl Documentation</span>
                  <span className="resource-description">Complete API reference and advanced guides</span>
                </div>
                <ExternalLink size={16} />
              </a>
              <a
                href="https://github.com/FullControlXYZ/fullcontrol"
                target="_blank"
                rel="noopener noreferrer"
                className="resource-link"
              >
                <div className="resource-content">
                  <span className="resource-title">FullControl GitHub Repository</span>
                  <span className="resource-description">Source code, issues, and community contributions</span>
                </div>
                <ExternalLink size={16} />
              </a>
              <a
                href="https://github.com/FullControlXYZ/fullcontrol/blob/master/tutorials/overview.ipynb"
                target="_blank"
                rel="noopener noreferrer"
                className="resource-link"
              >
                <div className="resource-content">
                  <span className="resource-title">Jupyter Notebook Tutorials</span>
                  <span className="resource-description">Interactive tutorials and code examples</span>
                </div>
                <ExternalLink size={16} />
              </a>
            </div>
          </section>          {/* Help Section */}
          <section className="docs-section help-section">
            <h2>
              <span className="section-number">04. </span>
              Need Help?
            </h2>
            <p className="section-intro">
              Can't find what you're looking for? Here are some ways to get help:
            </p>
            <div className="help-grid">
              <div className="help-card">
                <div className="help-icon">
                  <Book />
                </div>
                <h3>Browse Examples</h3>
                <p>Check out the <Link href="/docs/examples" className="inline-link">examples gallery</Link> for inspiration and practical implementations.</p>
              </div>
              <div className="help-card">
                <div className="help-icon">
                  <Code />
                </div>
                <h3>Community Sketches</h3>
                <p>Browse community sketches on the <Link href="/" className="inline-link">homepage</Link> to see what others are creating.</p>
              </div>
              <div className="help-card">
                <div className="help-icon">
                  <ExternalLink />
                </div>
                <h3>Official Resources</h3>
                <p>Visit the official <a href="https://fullcontrol.xyz/" target="_blank" rel="noopener noreferrer" className="inline-link">FullControl website</a> for comprehensive documentation.</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </StandardLayout>
  );
}
